/**
 * PGD 4a: Flucloxacillin for infected insect bites and stings
 * Version: 1.0
 * Effective: 2024-01-31
 * Expiry: 2027-01-30
 *
 * QUANTITIES ARE PRE-VERIFIED LOOKUPS. DO NOT CALCULATE.
 */

import type { PGD } from "../types";

export const pgdFlucloxacillinImpetigo: PGD = {
  id: "pgd-3c-fluclox-impetigo",
  reference: "3c",
  version: "1.0",
  drugRef: "flucloxacillin",
  indication:
    "Widespread non-bullous impetigo — no penicillin allergy",
  validFrom: "2024-01-31",
  expiryDate: "2027-01-30",
  frameworkType: "pharmacy_first",

  population: {
    ageRange: { min: 1, max: 999 },
    pregnancyAllowed: true,
    breastfeedingAllowed: true,
    notes:
      "Pregnancy excluded if under 16 — gated in the pathway flow before drug selection. Widespread non-bullous impetigo only.",
  },

  duration: { days: 5 },

  doseBands: [
    {
      id: "fluclox-age1-under2",
      resolveBy: "age",
      ageRange: { min: 1, max: 1 },
      dose: 125,
      frequency: "FOUR times a day",
      comment: "Age 1 to under 2 — liquid only, no capsules",
      formulations: [
        {
          formulationId: "fluclox-250-5ml",
          instruction: "Give 2.5mL (125mg) FOUR times a day for 5 days",
          quantity: { bottles: 1, label: "1 × 100mL bottle" },
        },
        {
          formulationId: "fluclox-125-5ml",
          instruction: "Give 5mL (125mg) FOUR times a day for 5 days",
          quantity: { bottles: 1, label: "1 × 100mL bottle" },
        },
      ],
    },
    {
      id: "fluclox-age2-9",
      resolveBy: "age",
      ageRange: { min: 2, max: 9 },
      dose: 250,
      frequency: "FOUR times a day",
      formulations: [
        {
          formulationId: "fluclox-250-cap",
          instruction:
            "Take ONE 250mg capsule FOUR times a day for 5 days",
          quantity: { count: 20, label: "20 capsules" },
        },
        {
          formulationId: "fluclox-250-5ml",
          instruction: "Give 5mL (250mg) FOUR times a day for 5 days",
          quantity: { bottles: 1, label: "1 × 100mL bottle" },
        },
        {
          formulationId: "fluclox-125-5ml",
          instruction: "Give 10mL (250mg) FOUR times a day for 5 days",
          quantity: { bottles: 2, label: "2 × 100mL bottles" },
        },
      ],
    },
    {
      id: "fluclox-age10plus",
      resolveBy: "age",
      ageRange: { min: 10, max: 999 },
      dose: 500,
      frequency: "FOUR times a day",
      formulations: [
        {
          formulationId: "fluclox-500-cap",
          instruction:
            "Take ONE 500mg capsule FOUR times a day for 5 days",
          quantity: { count: 20, label: "20 capsules" },
        },
        {
          formulationId: "fluclox-250-cap",
          instruction:
            "Take TWO 250mg capsules FOUR times a day for 5 days",
          quantity: { count: 40, label: "40 capsules" },
        },
        {
          formulationId: "fluclox-250-5ml",
          instruction: "Take 10mL (500mg) FOUR times a day for 5 days",
          quantity: { bottles: 2, label: "2 × 100mL bottles" },
        },
        {
          formulationId: "fluclox-125-5ml",
          instruction: "Take 20mL (500mg) FOUR times a day for 5 days",
          quantity: { bottles: 4, label: "4 × 100mL bottles" },
        },
      ],
    },
  ],

  preSupplyChecks: [
    { id: "ex-consent-refused", question: "Has the patient/carer refused consent?" },
    {
      id: "ex-unable-swallow",
      question:
        "Unable to absorb oral medications and/or unable to swallow oral dosage formulations?",
    },
    { id: "ex-keto", question: "Ketogenic diet?" },
    {
      id: "ex-failed-abx",
      question:
        "Failed previous oral antimicrobial treatment for this episode of impetigo?",
    },
    {
      id: "ex-mrsa",
      question: "Previous or current known MRSA colonisation or infection?",
    },
    {
      id: "ex-liver-disease",
      question: "Previous or current history of liver disease?",
    },
    {
      id: "ex-fluclox-jaundice",
      question:
        "Previous history of flucloxacillin-associated jaundice or liver dysfunction?",
    },
    {
      id: "ex-ckd5",
      question: "Known CKD stage 5 (eGFR <15 mL/min/1.73m²)?",
    },
    {
      id: "ex-hagma-paracetamol",
      question:
        "At risk of HAGMA (e.g. malnutrition, sepsis, renal impairment) AND recently or currently taking paracetamol?",
    },
    { id: "ex-typhoid", question: "Oral typhoid vaccine within ±3 days?" },
    { id: "int-methotrexate", question: "Currently taking methotrexate?" },
    { id: "int-probenecid", question: "Currently taking probenecid?" },
  ],

  cautions: [
    {
      id: "caution-breastfeeding",
      text:
        "Breastfeeding — monitor nursing infant for GI disturbances, oral candida infection, hypersensitivity and rash",
    },
    {
      id: "caution-warfarin",
      text:
        "Coumarin anticoagulants (e.g. warfarin, acenocoumarol, phenindione) — INR may rise. Advise INR monitoring and to seek medical attention if any bleeding develops.",
    },
    {
      id: "caution-excipients",
      text:
        "Check excipients: lactose, sucrose, fructose, sorbitol (hereditary intolerances); aspartame (phenylketonuria)",
    },
  ],

  counselling: [
    "Take 1 hour before food or 2 hours after food",
    "Capsules should be swallowed whole",
    "Complete the full 5-day course even if symptoms improve",
    "Manage initial pain and swelling with appropriate OTC pain relief (paracetamol or ibuprofen) and cold compress",
    "Draw a line around the border of erythema (or take clear photos) and monitor",
    "Return to pharmacy if symptoms worsen at any time OR do not improve after completing the 5-day course",
    "Seek urgent help if signs of sepsis develop — call 999 or attend A&E",
  ],

  counsellingPoints: [
    { text: "Take 1 hour before food or 2 hours after food" },
    { text: "Capsules should be swallowed whole" },
    { text: "Complete the full 5-day course even if symptoms improve" },
    {
      text:
        "Manage initial pain and swelling with appropriate OTC pain relief (paracetamol or ibuprofen) and cold compress",
    },
    {
      text:
        "Draw a line around the border of erythema (or take clear photos) and monitor",
    },
    {
      text:
        "Return to pharmacy if symptoms worsen at any time OR do not improve after completing the 5-day course",
    },
    { text: "Seek urgent help if signs of sepsis develop — call 999 or attend A&E" },
    {
      text:
        "Children should be encouraged to swallow solid oral dose forms where possible",
      linkRefs: [
        "medicines-for-children-fluclox",
        "medicines-for-children-giving-capsules",
      ],
    },
  ],

  outcomeLinkRefs: ["bad-impetigo-leaflet", "nhs-impetigo"],
};
